import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import NoResults from '@Components/NoResults/NoResults';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import EdUploadFile from '@Components/UI/Inputs/EdUploadFile/EdUploadFile';
import SelectChip from '@Components/UI/Inputs/SelectChip/SelectChip';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import EdActiveStatus from '@Components/UI/Utilities/EdActiveStatus/EdActiveStatus';
import EdFab from '@Components/UI/Utilities/EdFab/EdFab';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { ActionsTableRow } from '@Components/MainTable/MainTable.styled';
import {
	BreadcrumbContext,
	NavMenuContext,
} from '@Components/UI/Layout/Layout';
import { getEducationalInfo } from '@Features/classrooms/educationalSlice';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import { TableHeader } from '@Features/generics/generics.type';
import { RouteGroup, SingleRoute } from '@Constants/routes';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { Divider, FlexLayout, GridLayout, Spacer } from '@Styled/utilities';
import { ChipFlexLayout } from '@Pages/ClassRooms/Components/AddEditClassroom.styled';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Avatar,
	AvatarGroup,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	Icon,
	IconButton,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	SpeedDial,
	SpeedDialAction,
	Switch as MuiSwitch,
} from '@mui/material';
import { TooltipTitle } from '@Utils/TooltipTitle';
import educationBadge, { educationFullNames } from '@Utils/educationBadge';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useParams,
	useRouteMatch,
} from 'react-router-dom';
import styled from 'styled-components';
import {
	DragDropContext,
	Draggable,
	Droppable,
	DropResult,
} from 'react-beautiful-dnd';
import StandaloneLectureManagement, {
	AddStandaloneLecture,
} from './StandaloneLectureManagement';
import StandaloneCourseUsers from './StandaloneCourseUsers';
import StandaloneCourseCurriculum from './StandaloneCourseCurriculum';
import StandaloneCourseAnalytics from './StandaloneCourseAnalytics';
import StandaloneSubmissions from './Submissions/StandaloneSubmissions';
import StandaloneQuestionsAndAnswers from './QuestionsAndAnswers/StandaloneQuestionsAndAnswers';
import ProductEnrollmentInvoices from '@ContentManagement/ProductEnrollmentInvoices';
import useStandalonePageLoader from './useStandalonePageLoader';
import StandaloneLoadingProvider from './StandaloneLoadingProvider';
import useAdminActionPermissions from './useAdminActionPermissions';
// relative, not @Utils: the instructor app re-exports this page
import { formatCurriculumPosition } from '../../utils/curriculumPosition';

type EducationItem = { id: number; name: string };
type Instructor = {
	user_id?: number;
	userId?: number;
	user?: { id?: number; first_name?: string; last_name?: string };
};
type Lecture = {
	id: number;
	name: string;
	code?: string;
	description?: string;
	order: number;
	active: boolean;
	section_id?: number;
	sectionId?: number;
	expiry_date?: string | null;
	expiry_period?: number | null;
};
type Section = { id: number; name: string; order: number; courses: Lecture[] };
type ContentTab = { id: number; name: string; order: number; sections: Section[] };
type StandaloneCourse = {
	id: number;
	title: string;
	label: string;
	code: string;
	description: string;
	instructor_id: number;
	instructorId?: number;
	category_id: number;
	categoryId?: number;
	instructor?: Instructor;
	category?: EducationItem;
	language: string;
	sub_type: string;
	type: 'online';
	thumbnail: string;
	status: string;
	active: boolean;
	accessible: boolean;
	buyable: boolean;
	featured?: boolean;
	featured_order?: number | null;
	order: number;
	price: number;
	old_price?: number | null;
	payment_methods_allowed: string;
	expiry_date?: string | null;
	expiry_period?: number | null;
	educationTypes?: EducationItem[];
	educationYears?: EducationItem[];
	educationLanguages?: EducationItem[];
	educationSections?: EducationItem[];
	tabs?: ContentTab[];
	// Lectures whose section was deleted (courses.section_id SET NULL) — not
	// reachable through tabs, served separately by the details endpoint.
	unsectioned_lectures?: Lecture[];
	$extras?: { enrollments_count?: number };
};

type CourseForm = Omit<
	Partial<StandaloneCourse>,
	'educationTypes' | 'educationYears' | 'educationLanguages' | 'educationSections'
> & {
	educationTypes: number[];
	educationYears: number[];
	educationLanguages: number[];
	educationSections: number[];
};

const TABLE_HEADS: TableHeader[] = [
	{ displayName: 'TITLE', fieldName: 'title', width: '18%' },
	{ displayName: 'INSTRUCTOR', fieldName: 'instructor_id', width: '14%' },
	{ displayName: 'YEAR', fieldName: 'education_years', canSort: false, width: '10%' },
	{ displayName: 'STATUS', fieldName: 'status', width: '9%' },
	{ displayName: 'ORDER', fieldName: 'order', width: '7%' },
	{ displayName: 'PRICE', fieldName: 'price', width: '9%' },
	{ displayName: 'PAYMENT', fieldName: 'payment_methods_allowed', width: '11%' },
	{ displayName: 'FEATURED', fieldName: 'featured', canSort: false, width: '13%' },
	{ displayName: 'ACTIVE', fieldName: 'active', canSort: false, width: '7%' },
];

const paymentOptions = [
	{ value: '*', label: 'All' },
	{ value: 'wallet', label: 'Wallet' },
	{ value: 'scratchcard', label: 'Scratchcard' },
];

const apiData = <T,>(response: any): T => response.data.data as T;

const tableCellStyles = {
	fontFamily: 'Roboto',
	fontSize: '1rem',
	padding: '0 1.063rem 0',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	cursor: 'pointer',
} as const;

const StandaloneCourses: React.FC = () => {
	const { path, url } = useRouteMatch();
	// authoring screens (create/edit course, add lecture) exist only in the
	// admin portal — deep links bounce back to the list in the instructor one
	const isAdminPortal = api.defaults.baseURL?.includes('/admin/') === true;
	return (
		<StandaloneLoadingProvider>
			<Switch>
				<Route exact path={path} component={StandaloneCoursesList} />
				<Route
					exact
					path={`${path}/create`}
					render={() => (isAdminPortal ? <StandaloneCourseForm /> : <Redirect to={url} />)}
				/>
				<Route
					exact
					path={`${path}/:standaloneLabel/create/lecture/info`}
					render={() => (isAdminPortal ? <AddStandaloneLecture /> : <Redirect to={url} />)}
				/>
				<Route
					path={`${path}/:standaloneLabel/lecture/:id`}
					component={StandaloneLectureManagement}
				/>
				<Route
					exact
					path={`${path}/:label/edit`}
					render={() => (isAdminPortal ? <StandaloneCourseForm /> : <Redirect to={url} />)}
				/>
				<Route exact path={`${path}/:label`} component={StandaloneCourseManagement} />
				<Route path={`${path}/:label/:page`} component={StandaloneCourseManagement} />
				<Redirect to={url} />
			</Switch>
		</StandaloneLoadingProvider>
	);
};

const StandaloneCoursesList: React.FC = () => {
	const history = useHistory();
	const { url } = useRouteMatch();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const { can, canAuthor } = useAdminActionPermissions();
	// Authoring (create/edit/archive/featured) is admin-only — the instructor
	// portal only operates: the active toggle stays on the operating gate.
	const canCreate = canAuthor('c-cl');
	const canUpdate = can('u-cl');
	const canCurate = canAuthor('u-cl');
	const canDelete = canAuthor('d-cl');
	const [items, setItems] = useState<StandaloneCourse[]>([]);
	const [meta, setMeta] = useState<any>({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
	const [filters, setFilters] = useState<any[]>([]);
	const [archiveLabel, setArchiveLabel] = useState<string>();
	const openManagement = useCallback((label: string) => {
		history.push(`${url}/${label}`);
	}, [history, url]);

	const load = useCallback(async (request: Partial<onPageChangeRequestMeta> = {}) => {
		try {
			const from =
				request.startDate && !Number.isNaN(request.startDate.getTime())
					? request.startDate
					: undefined;
			const to =
				request.endDate && !Number.isNaN(request.endDate.getTime())
					? request.endDate
					: undefined;
			await withLoading(async () => {
				const response = await api.post('standalone-courses/all', {
					page: request.activePage || 1,
					perPage: request.perPage || 10,
					query: request.query || '',
					filters: request.filters || [],
					sortBy: request.sortBy || undefined,
					from,
					to,
				});
				setItems(apiData<StandaloneCourse[]>(response));
				setMeta(response.data.meta || {});
				setFilters(response.data.filters || []);
			});
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to load standalone courses');
		}
	}, [displaySnackbar, withLoading]);

	// No mount-time load() here: Pagination fires the initial page-change with
	// the URL's filters/query/page. A bare load() raced it with an unfiltered
	// request, and whichever response landed last won — so a filtered URL
	// (deep link or back-navigation) often rendered unfiltered rows under
	// the filter chips.

	const updateFeatured = async (course: StandaloneCourse, changes: { featured?: boolean; featured_order?: number | null }) => {
		try {
			await api.put('standalone-courses/update', { id: course.id, ...changes });
			setItems((current) =>
				current.map((item) => (item.id === course.id ? { ...item, ...changes } : item))
			);
			if (changes.featured !== undefined) {
				displaySnackbar('success', `Course ${changes.featured ? 'featured on' : 'removed from'} landing carousel`);
			}
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to update featured state');
		}
	};

	const updateActive = async (course: StandaloneCourse, active: boolean) => {
		try {
			await withLoading(() =>
				api.put('standalone-courses/update', { id: course.id, active })
			);
			setItems((current) =>
				current.map((item) => (item.id === course.id ? { ...item, active } : item))
			);
			displaySnackbar('success', `Standalone course ${active ? 'activated' : 'deactivated'} successfully`);
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to update active state');
		}
	};

	const archive = async (close: boolean, confirm?: boolean) => {
		if (confirm && archiveLabel) {
			try {
				await withLoading(() =>
					api.delete('standalone-courses/archive', {
						data: { label: archiveLabel },
					})
				);
				displaySnackbar('success', 'Standalone course archived successfully');
				load();
			} catch (error: any) {
				displaySnackbar('error', error.response?.data?.message || 'Unable to archive standalone course');
			}
		}
		setArchiveLabel(undefined);
	};

	return (
		<>
			{canCreate && (
				<Actions><Button variant="contained" startIcon={<AddIcon />} onClick={() => history.push(`${url}/create`)}>Add Standalone Course</Button></Actions>
			)}
			<EdConfirmModal open={Boolean(archiveLabel)} handleClose={archive} text="Are you sure you want to archive this standalone course?" />
			<MainTable
				onPageChangeRequest={load}
				tableFilters={filters}
				totalPages={Number(meta.last_page || 1)}
				total={Number(meta.total || items.length)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={Number(meta.current_page || 1)}
				tableHeads={TABLE_HEADS}
				tableMinWidth="55rem"
				$hasActions
				renderItems={() => items.length === 0 ? (
					<MainTableRow><TableCell colSpan={TABLE_HEADS.length + 1}><NoResults /></TableCell></MainTableRow>
				) : items.map((course) => (
					<MainTableRow
						key={course.id}
						hover
						sx={{ '&:active': { backgroundColor: '#6c63ff' } }}
					>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(course.label)}>{course.title}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(course.label)}>{`${course.instructor?.user?.first_name || ''} ${course.instructor?.user?.last_name || ''}`.trim()}</TableCell>
						<TableCell sx={{ ...tableCellStyles, overflow: 'unset' }} onClick={() => openManagement(course.label)}>
							<Tooltip title={TooltipTitle(educationFullNames(course.educationYears as any))} placement="top">
								<AvatarGroup
									max={2}
									sx={{
										justifyContent: 'flex-end',
										// Pill, not a fixed circle — badge texts must stay inside.
										'& .MuiAvatar-root': {
											width: 'auto',
											minWidth: 22,
											height: 22,
											padding: '0 6px',
											borderRadius: '11px',
											fontSize: '9px',
										},
									}}
								>
									{((course.educationYears as any) ?? []).map((edu: any) => {
										const badge = educationBadge(edu);
										return (
											<Avatar
												key={edu.id}
												alt="education year"
												sx={{ backgroundColor: badge.color, fontFamily: 'Roboto' }}
											>
												{badge.short}
											</Avatar>
										);
									})}
								</AvatarGroup>
							</Tooltip>
						</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(course.label)}>{course.status}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(course.label)}>{course.order}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(course.label)}>{Number(course.price) === 0 ? 'Free' : course.price}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(course.label)}>{course.payment_methods_allowed === '*' ? 'All' : course.payment_methods_allowed}</TableCell>
						<TableCell sx={tableCellStyles}>
							<FlexLayout display="flex" alignItems="center" gridGap="0.5rem">
								{canCurate ? (
									<Tooltip placement="top" title={course.featured ? 'Remove from landing carousel' : 'Feature on landing carousel'}>
										<MuiSwitch
											size="small"
											checked={Boolean(course.featured)}
											onClick={(event) => event.stopPropagation()}
											onChange={(event) => updateFeatured(course, { featured: event.target.checked })}
										/>
									</Tooltip>
								) : (
									<MuiSwitch size="small" checked={Boolean(course.featured)} disabled />
								)}
								{course.featured && (
									<TextField
										size="small"
										type="number"
										disabled={!canCurate}
										inputProps={{ min: 1, style: { width: '3rem', padding: '0.25rem 0.5rem' } }}
										defaultValue={course.featured_order ?? ''}
										placeholder="#"
										onClick={(event) => event.stopPropagation()}
										onBlur={(event) => {
											const value = event.target.value === '' ? null : Number(event.target.value);
											if (value !== (course.featured_order ?? null)) {
												updateFeatured(course, { featured_order: value });
											}
										}}
									/>
								)}
							</FlexLayout>
						</TableCell>
						<TableCell sx={tableCellStyles}>
							{canUpdate ? (
								<Tooltip
									placement="top"
									title={course.active ? 'Deactivate' : 'Activate'}
								>
									<Button
										onClick={(event) => {
											event.stopPropagation();
											updateActive(course, !course.active);
										}}
									>
										{course.active ? (
											<ActiveCheckIcon>check</ActiveCheckIcon>
										) : (
											<InactiveCloseIcon>close</InactiveCloseIcon>
										)}
									</Button>
								</Tooltip>
							) : course.active ? (
								<ActiveCheckIcon>check</ActiveCheckIcon>
							) : (
								<InactiveCloseIcon>close</InactiveCloseIcon>
							)}
						</TableCell>
						<ActionsTableRow
							sx={{
							padding: '0 1.063rem 0',
							position: 'sticky',
							right: 0,
							background: 'linear-gradient(260deg, white, white, transparent)',
						}}
						>
							{(canCurate || canDelete) && (
								<SpeedDial
									FabProps={{
										disableRipple: true,
										disableTouchRipple: true,
										focusRipple: false,
										disableFocusRipple: true,
										style: {
											borderRadius: 0,
											boxShadow: '0 0 0 0',
											backgroundColor: 'transparent',
											color: '#000',
										},
									}}
									direction="left"
									ariaLabel="Standalone course actions"
									icon={<MoreVertIcon />}
								>
									{canDelete && (
										<SpeedDialAction
											key="archive"
											icon={<StyledDeleteIcon />}
											tooltipTitle="Delete"
											onClick={(event) => {
												event.stopPropagation();
												setArchiveLabel(course.label);
											}}
										/>
									)}
									{canCurate && (
										<SpeedDialAction
											key="edit"
											icon={<StyledEditIcon />}
											tooltipTitle="Edit"
											onClick={(event) => {
												event.stopPropagation();
												history.push(`${url}/${course.label}/edit`);
											}}
										/>
									)}
								</SpeedDial>
							)}
						</ActionsTableRow>
					</MainTableRow>
				))}
			/>
		</>
	);
};

const defaultForm: CourseForm = {
	title: '', label: '', code: '', description: '',
	instructor_id: '' as unknown as number, category_id: '' as unknown as number,
	language: 'Arabic', sub_type: 'class', type: 'online', thumbnail: '', status: 'open',
	active: true, accessible: true, buyable: true, order: 1,
	price: 0, old_price: null, payment_methods_allowed: '*', expiry_date: null,
	expiry_period: null, educationTypes: [], educationYears: [], educationLanguages: [], educationSections: [],
};

const StandaloneCourseForm: React.FC = () => {
	const { label } = useParams<{ label?: string }>();
	const history = useHistory();
	const dispatch = useAppThunkDispatch();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const { dropdownInstructors, dropdownCategories } = useTypedSelector((state) => state.dropdowns);
	const { educational_info } = useTypedSelector((state) => state.educational);
	const isAdminPortal = api.defaults.baseURL?.includes('/admin/');
	const { control, handleSubmit, reset, watch, setValue, formState: { isSubmitting, dirtyFields } } = useForm<CourseForm>({ defaultValues: defaultForm });
	const [loadedCourse, setLoadedCourse] = useState<StandaloneCourse>();
	const hydratedCourseLabel = useRef<string>();
	const selectedTypes = watch('educationTypes') || [];
	const selectedYears = watch('educationYears') || [];
	const selectedLanguages = watch('educationLanguages') || [];
	const selectedSections = watch('educationSections') || [];
	const featured = Boolean(watch('featured'));
	const price = Number(watch('price') || 0);
	// Cascading (clearing/pruning child selections) is only correct as a
	// reaction to the USER changing a parent field — never to data arriving.
	// Without this gate, a render where the dropdown tree lags the hydrated
	// course silently clears saved selections and blocks saving.
	const typesDirty = Boolean(dirtyFields?.educationTypes);
	const yearsDirty = Boolean(dirtyFields?.educationYears);

	useEffect(() => { dispatch(getEducationalInfo()); }, [dispatch]);
	useEffect(() => {
		if (selectedTypes.length > 0 || !typesDirty) return;
		setValue('educationYears', []);
		setValue('educationLanguages', []);
	}, [selectedTypes.length, typesDirty, setValue]);
	useEffect(() => {
		if (selectedYears.length > 0 || (!typesDirty && !yearsDirty)) return;
		setValue('educationSections', []);
	}, [selectedYears.length, typesDirty, yearsDirty, setValue]);
	useEffect(() => {
		if (!label) return;
		withLoading(async () => {
			const response = await api.post('standalone-courses', { label });
			hydratedCourseLabel.current = undefined;
			setLoadedCourse(apiData<StandaloneCourse>(response));
		}).catch((error) => {
			displaySnackbar('error', error.response?.data?.message || 'Unable to load standalone course');
		});
	}, [displaySnackbar, label, withLoading]);
	useEffect(() => {
		if (!label || !loadedCourse || hydratedCourseLabel.current === label) return;
		if (isAdminPortal && dropdownInstructors.length === 0) return;
		if (dropdownCategories.length === 0) return;

		const instructorName = loadedCourse.instructor?.user;
		const instructorId = loadedCourse.instructor_id
			?? loadedCourse.instructorId
			?? loadedCourse.instructor?.user_id
			?? loadedCourse.instructor?.userId
			?? instructorName?.id
			?? dropdownInstructors.find((item: any) =>
				item.first_name === instructorName?.first_name
				&& item.last_name === instructorName?.last_name
			)?.id;
		const categoryId = loadedCourse.category_id
			?? loadedCourse.categoryId
			?? loadedCourse.category?.id
			?? dropdownCategories.find((item: any) =>
				item.name === loadedCourse.category?.name
			)?.id;

		reset({
			...defaultForm,
			...loadedCourse,
			instructor_id: instructorId === undefined ? defaultForm.instructor_id : Number(instructorId),
			category_id: categoryId === undefined ? defaultForm.category_id : Number(categoryId),
			educationTypes: loadedCourse.educationTypes?.map((item) => item.id) || [],
			educationYears: loadedCourse.educationYears?.map((item) => item.id) || [],
			educationLanguages: loadedCourse.educationLanguages?.map((item) => item.id) || [],
			educationSections: loadedCourse.educationSections?.map((item) => item.id) || [],
		});
		hydratedCourseLabel.current = label;
	}, [
		dropdownCategories,
		dropdownInstructors,
		isAdminPortal,
		label,
		loadedCourse,
		reset,
	]);

	const years = useMemo(() => educational_info.filter((item: any) => selectedTypes.includes(item.id)).flatMap((item: any) => item.educationYears || []).filter((item: any, index: number, all: any[]) => all.findIndex((other) => other.id === item.id) === index), [educational_info, selectedTypes]);
	const languages = useMemo(() => educational_info.filter((item: any) => selectedTypes.includes(item.id)).flatMap((item: any) => item.educationLanguages || []).filter((item: any, index: number, all: any[]) => all.findIndex((other) => other.id === item.id) === index), [educational_info, selectedTypes]);
	const sections = useMemo(() => years.filter((item: any) => selectedYears.includes(item.id)).flatMap((item: any) => item.educationSections || []).filter((item: any, index: number, all: any[]) => all.findIndex((other) => other.id === item.id) === index), [years, selectedYears]);

	// Deselecting a parent must drop the child selections that hung off it;
	// otherwise the ids linger as label-less chips and reach the payload.
	// Gated on the parent being USER-dirty: pruning against a dropdown tree
	// that is merely loading (or lagging the record) must never discard the
	// hydrated values — that wiped Years/Languages on edit and blocked saving.
	useEffect(() => {
		if (!educational_info.length || !typesDirty) return;
		const validYears = new Set(years.map((item: any) => item.id));
		const validLanguages = new Set(languages.map((item: any) => item.id));
		if (selectedYears.some((id) => !validYears.has(id))) {
			setValue('educationYears', selectedYears.filter((id) => validYears.has(id)));
		}
		if (selectedLanguages.some((id) => !validLanguages.has(id))) {
			setValue('educationLanguages', selectedLanguages.filter((id) => validLanguages.has(id)));
		}
	}, [educational_info.length, years, languages, selectedYears, selectedLanguages, typesDirty, setValue]);
	useEffect(() => {
		if (!educational_info.length || (!typesDirty && !yearsDirty)) return;
		const validSections = new Set(sections.map((item: any) => item.id));
		if (selectedSections.some((id) => !validSections.has(id))) {
			setValue('educationSections', selectedSections.filter((id) => validSections.has(id)));
		}
	}, [educational_info.length, sections, selectedSections, typesDirty, yearsDirty, setValue]);

	const removeEducationValue = (
		field: 'educationTypes' | 'educationYears' | 'educationLanguages' | 'educationSections',
		value: number
	) => {
		const currentValues = watch(field) || [];
		setValue(field, currentValues.filter((currentValue) => currentValue !== value), {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const save = async (values: CourseForm) => {
		if (Number(values.old_price || 0) > 0 && Number(values.old_price) <= Number(values.price || 0)) {
			displaySnackbar('error', 'Old price must be greater than price');
			return;
		}
		try {
			const payload: any = {
				...values,
				category_id: Number(values.category_id),
			};
			if (isAdminPortal) payload.instructor_id = Number(values.instructor_id);
			delete payload.type;
			if (price === 0) payload.old_price = null;
			if (label && values.featured === false) payload.featured_order = null;
			const response = await withLoading(() =>
				label
					? api.put('standalone-courses/update', { ...payload, id: values.id })
					: api.post('standalone-courses/create', payload)
			);
			const course = apiData<StandaloneCourse>(response);
			displaySnackbar('success', `Standalone course ${label ? 'updated' : 'created'} successfully`);
			history.push(`/dashboard/classrooms/standalone-courses/${course.label || values.label}`);
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to save standalone course');
		}
	};

	return (
		<StandaloneFormContainer>
			<FormTitle>{label ? 'Edit' : 'Add'} Standalone Course</FormTitle>
			<form onSubmit={handleSubmit(save)}>
				<Divider label="Basic Information" />
				<FormGrid>
					<EdFormControl control={control} required name="title" label="Title" />
					{isAdminPortal && <EdFormControl control={control} required select name="instructor_id" label="Instructor">{dropdownInstructors.map((item: any) => <MenuItem key={item.id} value={item.id}>{item.first_name} {item.last_name}</MenuItem>)}</EdFormControl>}
					<EdFormControl control={control} required select name="category_id" label="Subject">{dropdownCategories.map((item: any) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</EdFormControl>
					<EdFormControl control={control} required multiline name="description" label="Description" />
				</FormGrid>
				<Divider label="Course Identity" />
				<FormGrid>
					<EdFormControl control={control} required name="label" label="Label" />
					<EdFormControl control={control} required name="code" label="Code" />
					<EdFormControl control={control} disabled name="type" label="Type" />
					<EdFormControl control={control} required select name="sub_type" label="Sub Type">{['exam', 'revision', 'class'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</EdFormControl>
					<EdFormControl control={control} required select name="language" label="Language">{['Arabic', 'English', 'French', 'German'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</EdFormControl>
					<EdFormControl control={control} required select name="status" label="Status">{['open', 'closed', 'soon'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</EdFormControl>
				</FormGrid>
				<Divider label="Commerce & Availability" />
				<FormGrid>
					<EdFormControl control={control} required type="number" name="order" label="Order" />
					{label && (
						<EdFormControl
							control={control}
							type="number"
							name="featured_order"
							label="Featured Order"
							disabled={!featured}
						/>
					)}
					<EdFormControl control={control} required type="number" name="price" label="Price" />
					<EdFormControl control={control} type="number" disabled={price === 0} name="old_price" label="Price before discount" />
					<EdFormControl control={control} required select name="payment_methods_allowed" label="Payment">{paymentOptions.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}</EdFormControl>
					<EdFormControl control={control} type="date" name="expiry_date" label="Course activation deadline" InputLabelProps={{ shrink: true }} />
					<EdFormControl control={control} type="number" name="expiry_period" label="Activation period (days)" />
				</FormGrid>
				<SwitchGrid>
					<EdFormControl control={control} switch name="active" label="Active" />
					<EdFormControl control={control} switch name="accessible" label="Accessible" />
					<EdFormControl control={control} switch name="buyable" label="Buyable" />
					{label && <EdFormControl control={control} switch name="featured" label="Featured" />}
				</SwitchGrid>
				<Divider label="Cover & Media" />
				<CoverGrid>
					<EdUploadFile
						required
						folder="classrooms"
						uploadLabel="UPLOAD"
						uploadIcon={<Icon>add</Icon>}
						control={control}
						name="thumbnail"
						label="Cover"
						optimize={false}
						onUploadError={(error: any) => displaySnackbar(
							'error',
							error?.response?.data?.message || error?.message || 'Unable to upload cover'
						)}
					/>
				</CoverGrid>
				<Divider label="Educational Info." />
				<FormGrid>
					<EdFormControl
						control={control}
						required
						select
						multiple
						name="educationTypes"
						label="Education Types"
						renderValues={() => (
							<div>
								{selectedTypes.map((id) => (
									<SelectChip
										key={`education-type-${id}`}
										label={educational_info.find((item: any) => item.id === id)?.name}
										onDelete={() => removeEducationValue('educationTypes', id)}
									/>
								))}
							</div>
						)}
						SelectProps={{
							multiple: true,
							renderValue: () => (
								<ChipFlexLayout>
									{selectedTypes.map((id) => educational_info.find((item: any) => item.id === id)?.name).filter(Boolean).join(', ')}
								</ChipFlexLayout>
							),
						}}
					>
						{educational_info.map((item: any) => (
							<MenuItem key={item.id} value={item.id}>
								<Checkbox checked={selectedTypes.includes(item.id)} />
								{item.name}
							</MenuItem>
						))}
					</EdFormControl>
					<EdFormControl
						control={control}
						required
						disabled={selectedTypes.length === 0}
						select
						multiple
						name="educationYears"
						label="Education Years"
						renderValues={() => (
							<div>
								{selectedYears.map((id) => (
									<SelectChip
										key={`education-year-${id}`}
										label={years.find((item: any) => item.id === id)?.name}
										onDelete={() => removeEducationValue('educationYears', id)}
									/>
								))}
							</div>
						)}
						SelectProps={{
							multiple: true,
							renderValue: () => (
								<ChipFlexLayout>
									{selectedYears.map((id) => years.find((item: any) => item.id === id)?.name).filter(Boolean).join(', ')}
								</ChipFlexLayout>
							),
						}}
					>
						{years.map((item: any) => (
							<MenuItem key={item.id} value={item.id}>
								<Checkbox checked={selectedYears.includes(item.id)} />
								{item.name}
							</MenuItem>
						))}
					</EdFormControl>
					<EdFormControl
						control={control}
						required
						disabled={selectedTypes.length === 0}
						select
						multiple
						name="educationLanguages"
						label="Education Languages"
						renderValues={() => (
							<div>
								{selectedLanguages.map((id) => (
									<SelectChip
										key={`education-language-${id}`}
										label={languages.find((item: any) => item.id === id)?.name}
										onDelete={() => removeEducationValue('educationLanguages', id)}
									/>
								))}
							</div>
						)}
						SelectProps={{
							multiple: true,
							renderValue: () => (
								<ChipFlexLayout>
									{selectedLanguages.map((id) => languages.find((item: any) => item.id === id)?.name).filter(Boolean).join(', ')}
								</ChipFlexLayout>
							),
						}}
					>
						{languages.map((item: any) => (
							<MenuItem key={item.id} value={item.id}>
								<Checkbox checked={selectedLanguages.includes(item.id)} />
								{item.name}
							</MenuItem>
						))}
					</EdFormControl>
					<EdFormControl
						control={control}
						disabled={selectedYears.length === 0}
						select
						multiple
						name="educationSections"
						label="Education Sections"
						renderValues={() => (
							<div>
								{selectedSections.map((id) => (
									<SelectChip
										key={`education-section-${id}`}
										label={sections.find((item: any) => item.id === id)?.name}
										onDelete={() => removeEducationValue('educationSections', id)}
									/>
								))}
							</div>
						)}
						SelectProps={{
							multiple: true,
							renderValue: () => (
								<ChipFlexLayout>
									{selectedSections.map((id) => sections.find((item: any) => item.id === id)?.name).filter(Boolean).join(', ')}
								</ChipFlexLayout>
							),
						}}
					>
						{sections.map((item: any) => (
							<MenuItem key={item.id} value={item.id}>
								<Checkbox checked={selectedSections.includes(item.id)} />
								{item.name}
							</MenuItem>
						))}
					</EdFormControl>
				</FormGrid>
				<Actions><Button type="button" onClick={() => history.goBack()}>Cancel</Button><Button disabled={isSubmitting} type="submit" variant="contained">Save</Button></Actions>
			</form>
		</StandaloneFormContainer>
	);
};

const managementRoute = (
	title: string,
	route: string,
	code: string
): SingleRoute => ({ title, route, code, component: null });

const StandaloneCourseManagement: React.FC = () => {
	const { label, page } = useParams<{ label: string; page?: string }>();
	const [course, setCourse] = useState<StandaloneCourse>();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const { setCurrentRouteGroup, setBaseURL } = useContext(NavMenuContext);
	const { addLink, removeLink } = useContext(BreadcrumbContext);
	const isAdminPortal = api.defaults.baseURL?.includes('/admin/');
	const managementRouteGroup = useMemo<RouteGroup>(() => ({
		title: 'Standalone Course',
		route: 'standalone-courses/:label',
		code: '',
		component: null,
		isRouted: true,
		isParent: true,
		overviewRoute: 'overview',
		routes: isAdminPortal
			? [
				managementRoute('Curriculum', 'curriculum', 'v-cur-cl'),
				managementRoute('Users', 'users', 'v-enrl-cl'),
				managementRoute('Analytics', 'analytics', 'v-anlt-cl'),
				managementRoute('Invoices', 'invoices', 'v-anlt-cl'),
			]
			: [
				managementRoute('Curriculum', 'curriculum', 'course_content'),
				managementRoute('Users', 'users', 'classroom_tab'),
				managementRoute('Q & A', 'questions-and-answers', 'discussions'),
				managementRoute('Analytics', 'analytics', 'v-anlt-cl'),
				managementRoute('Invoices', 'invoices', 'v-anlt-cl'),
				managementRoute('Submissions', 'submissions', 'submissions'),
			],
	}), [isAdminPortal]);
	const managementPages = useMemo(
		() => ['overview', ...managementRouteGroup.routes.map((route) => route.route)],
		[managementRouteGroup]
	);
	const activePage = page || 'overview';
	const load = useCallback(async () => {
		try {
			await withLoading(async () => {
				const response = await api.post('standalone-courses', { label });
				setCourse(apiData<StandaloneCourse>(response));
			});
		}
		catch (error: any) { displaySnackbar('error', error.response?.data?.message || 'Unable to load standalone course'); }
	}, [displaySnackbar, label, withLoading]);
	useEffect(() => { load(); }, [load]);
	useEffect(() => {
		setCurrentRouteGroup?.(managementRouteGroup);
		setBaseURL?.(`/dashboard/classrooms/standalone-courses/${label}`);
	}, [label, managementRouteGroup, setBaseURL, setCurrentRouteGroup]);
	useEffect(() => {
		if (!course) return;
		addLink?.({
			route: `/dashboard/classrooms/standalone-courses/${label}`,
			title: course.title,
			routeHash: 'standalone-course',
		});
		return () => removeLink?.('standalone-course');
	}, [addLink, course, label, removeLink]);
	if (!managementPages.includes(activePage)) return <Redirect to={`/dashboard/classrooms/standalone-courses/${label}`} />;
	return (
		<>
			{course && <ManagementTitle>{course.title}</ManagementTitle>}
			{course && activePage === 'overview' && <StandaloneOverview course={course} reload={load} />}
			{course && activePage === 'curriculum' && <StandaloneCourseCurriculum course={course} reload={load} />}
			{course && activePage === 'users' && <StandaloneCourseUsers courseId={course.id} price={Number(course.price || 0)} />}
			{course && activePage === 'analytics' && <StandaloneCourseAnalytics course={course} />}
			{course && activePage === 'invoices' && <Invoices course={course} />}
			{course && activePage === 'questions-and-answers' && <StandaloneQuestionsAndAnswers course={course} />}
			{course && !isAdminPortal && activePage === 'submissions' && <StandaloneSubmissions course={course} />}
		</>
	);
};

const LECTURE_TABLE_HEADS: TableHeader[] = [
	{ displayName: 'NAME', fieldName: 'name', width: '20rem' },
	{ displayName: 'SECTION', fieldName: 'section', width: '18rem' },
	{ displayName: 'ORDER', fieldName: 'order', width: '8rem' },
	{ displayName: 'CODE', fieldName: 'code', width: '16rem' },
	{ displayName: 'ACTIVE', fieldName: 'active', canSort: false, width: '8rem' },
];

const StandaloneOverview: React.FC<{
	course: StandaloneCourse;
	reload: () => void;
}> = ({ course, reload }) => {
	const history = useHistory();
	const { url } = useRouteMatch();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	// Lecture authoring (create/edit/status/delete) is admin-only — the
	// instructor portal views lectures without acting on them.
	const { canAuthor } = useAdminActionPermissions();
	const canCreateLecture = canAuthor('c-cr');
	const canUpdateLecture = canAuthor('u-cr');
	const canDeleteLecture = canAuthor('d-cr');
	const [query, setQuery] = useState('');
	const [archiveLecture, setArchiveLecture] = useState<Lecture>();
	const sections = useMemo(
		() =>
			(course.tabs || []).flatMap((tab, tabIndex) =>
				(tab.sections || []).map((section) => ({ ...section, tabOrder: tab.order ?? tabIndex + 1 }))
			),
		[course.tabs]
	);
	const lectures = useMemo(
		() => sections
			.flatMap((section) => (section.courses || []).map((lecture) => ({
				...lecture,
				sectionName: section.name,
				// tab › section › position inside the section
				position: formatCurriculumPosition(section.tabOrder, section.order, lecture.order),
			})))
			// Lectures whose section was deleted list with "-" (legacy-classroom
			// convention) so they stay editable and re-assignable to a section.
			.concat((course.unsectioned_lectures || []).map((lecture) => ({
				...lecture,
				sectionName: '-',
				position: formatCurriculumPosition(null, null, null),
			})))
			.filter((lecture) => lecture.name.toLowerCase().includes(query.toLowerCase())),
		[course.unsectioned_lectures, query, sections]
	);
	const confirmArchive = async (close: boolean, confirm?: boolean) => {
		if (confirm && archiveLecture) {
			try {
				await withLoading(() =>
					api.delete('standalone-courses/lectures/delete', {
						data: { id: archiveLecture.id },
					})
				);
				displaySnackbar('success', 'Lecture deleted successfully');
				reload();
			} catch (error: any) {
				displaySnackbar('error', error.response?.data?.message || 'Unable to delete Lecture');
			}
		}
		setArchiveLecture(undefined);
	};
	const updateLectureStatus = async (lecture: Lecture) => {
		try {
			await withLoading(() =>
				api.put('standalone-courses/lectures/update', {
					id: lecture.id,
					active: !lecture.active,
				})
			);
			displaySnackbar('success', `Lecture ${lecture.active ? 'deactivated' : 'activated'} successfully`);
			reload();
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to update Lecture status');
		}
	};

	return (
		<>
			{canCreateLecture && (
				<Actions>
					<Button variant="contained" startIcon={<AddIcon />} onClick={() => history.push(`${url}/create/lecture/info`)}>
						Add Lecture
					</Button>
				</Actions>
			)}
			<EdConfirmModal
				open={Boolean(archiveLecture)}
				handleClose={confirmArchive}
				text="Are you sure you want to delete this Lecture?"
			/>
			<MainTable
				onPageChangeRequest={({ query: nextQuery }) => setQuery(nextQuery || '')}
				tableFilters={[]}
				totalPages={1}
				total={lectures.length}
				RowsperPage={10}
				pageNum={1}
				tableHeads={LECTURE_TABLE_HEADS}
				tableMinWidth="78rem"
				$hasActions
				renderItems={() => lectures.length === 0 ? (
					<MainTableRow><TableCell colSpan={LECTURE_TABLE_HEADS.length + 1}><NoResults /></TableCell></MainTableRow>
				) : lectures.map((lecture) => (
					<MainTableRow key={lecture.id} hover>
						<TableCell sx={tableCellStyles} onClick={() => history.push(`${url}/lecture/${lecture.id}/edit/content`)}>{lecture.name}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => history.push(`${url}/lecture/${lecture.id}/edit/content`)}>{lecture.sectionName}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => history.push(`${url}/lecture/${lecture.id}/edit/content`)}>{lecture.position}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => history.push(`${url}/lecture/${lecture.id}/edit/content`)}>{lecture.code || '-'}</TableCell>
						<TableCell sx={tableCellStyles}>
							{canUpdateLecture ? (
								<Tooltip placement="top" title={lecture.active ? 'Deactivate' : 'Activate'}>
									<Button
										onClick={(event) => {
											event.stopPropagation();
											updateLectureStatus(lecture);
										}}
									>
										{lecture.active ? (
											<ActiveCheckIcon>check</ActiveCheckIcon>
										) : (
											<InactiveCloseIcon>close</InactiveCloseIcon>
										)}
									</Button>
								</Tooltip>
							) : lecture.active ? (
								<ActiveCheckIcon>check</ActiveCheckIcon>
							) : (
								<InactiveCloseIcon>close</InactiveCloseIcon>
							)}
						</TableCell>
						<ActionsTableRow sx={{ position: 'sticky', right: 0, background: 'linear-gradient(260deg, white, white, transparent)' }}>
							<SpeedDial
								FabProps={{ style: { borderRadius: 0, boxShadow: 'none', backgroundColor: 'transparent', color: '#000' } }}
								direction="left"
								ariaLabel="Lecture actions"
								icon={<MoreVertIcon />}
							>
								{canDeleteLecture && (
									<SpeedDialAction icon={<StyledDeleteIcon />} tooltipTitle="Delete" onClick={() => setArchiveLecture(lecture)} />
								)}
								<SpeedDialAction icon={<StyledEditIcon />} tooltipTitle="Manage" onClick={() => history.push(`${url}/lecture/${lecture.id}/edit/info`)} />
							</SpeedDial>
						</ActionsTableRow>
					</MainTableRow>
				))}
			/>
		</>
	);
};

const Curriculum: React.FC<{ course: StandaloneCourse; reload: () => void }> = ({ course, reload }) => {
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const [lectureSection, setLectureSection] = useState<number>();
	const [lectureName, setLectureName] = useState('');
	const [lectureCode, setLectureCode] = useState('');
	const [tabs, setTabs] = useState<ContentTab[]>(course.tabs || []);
	const [needReorder, setNeedReorder] = useState(false);
	useEffect(() => setTabs(course.tabs || []), [course.tabs]);
	const mutate = async (method: 'post' | 'patch' | 'delete' | 'put', url: string, payload: any, success: string) => {
		try { await api.request({ method, url, data: payload }); displaySnackbar('success', success); reload(); }
		catch (error: any) { displaySnackbar('error', error.response?.data?.message || 'Unable to update curriculum'); }
	};
	const move = <T,>(items: T[], from: number, to: number) => {
		const next = [...items];
		next.splice(to, 0, next.splice(from, 1)[0]);
		return next;
	};
	const onDragEnd = ({ source, destination, type }: DropResult) => {
		if (!destination) return;
		const next = tabs.map((tab) => ({
			...tab,
			sections: tab.sections.map((section) => ({ ...section, courses: [...section.courses] })),
		}));
		if (type === 'TABS') {
			setTabs(move(next, source.index, destination.index));
		} else if (type === 'SECTIONS') {
			const sourceTab = next.find((tab) => `tab-${tab.id}` === source.droppableId);
			const destinationTab = next.find((tab) => `tab-${tab.id}` === destination.droppableId);
			if (!sourceTab || !destinationTab) return;
			const [section] = sourceTab.sections.splice(source.index, 1);
			destinationTab.sections.splice(destination.index, 0, section);
			setTabs(next);
		} else if (type === 'LECTURES') {
			const allSections = next.flatMap((tab) => tab.sections);
			const sourceSection = allSections.find((section) => `section-${section.id}` === source.droppableId);
			const destinationSection = allSections.find((section) => `section-${section.id}` === destination.droppableId);
			if (!sourceSection || !destinationSection) return;
			const [lecture] = sourceSection.courses.splice(source.index, 1);
			destinationSection.courses.splice(destination.index, 0, lecture);
			setTabs(next);
		}
		setNeedReorder(true);
	};
	const saveOrder = async () => {
		try {
			await api.post('standalone-courses/curriculum/update', {
				standalone_course_id: course.id,
				tabs,
			});
			displaySnackbar('success', 'Curriculum re-ordered successfully');
			setNeedReorder(false);
			reload();
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to re-order curriculum');
		}
	};
	return (
		<StandaloneCurriculumPage>
			<FlexLayout alignItems="center">
				<EdFab
					onClick={() => mutate('post', 'standalone-courses/tabs/create', { standalone_course_id: course.id, name: `Tab ${(course.tabs?.length || 0) + 1}` }, 'Tab created')}
					size="small"
					edbackgroundColor="purple"
				>
					<EdIcon fontSize="medium">add</EdIcon>
				</EdFab>
				<Spacer mx="0.75rem" />
				<CurriculumFabLabel>Add Tab</CurriculumFabLabel>
			</FlexLayout>
			<Spacer marginBottom="2.25rem" />
			<DragDropContext onDragEnd={onDragEnd}>
			<Droppable droppableId="standalone-tabs" type="TABS">
			{(tabsDrop) => <div ref={tabsDrop.innerRef} {...tabsDrop.droppableProps}>
			{tabs.map((tab, tabIndex) => (
				<Draggable key={tab.id} draggableId={`tab-drag-${tab.id}`} index={tabIndex}>
				{(tabDrag) => <div ref={tabDrag.innerRef} {...tabDrag.draggableProps}>
				<StandaloneTabAccordion defaultExpanded>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<DndHandle {...tabDrag.dragHandleProps}>↕</DndHandle>
						<InlineEdit prefix={`Tab ${tabIndex + 1}`} initial={tab.name} onSave={(name) => mutate('patch', 'standalone-courses/tabs/update', { id: tab.id, name, order: tabIndex + 1 }, 'Tab updated')} onDelete={() => mutate('delete', 'standalone-courses/tabs/delete', { id: tab.id }, 'Tab deleted')} />
					</AccordionSummary>
					<AccordionDetails>
						<FlexLayout alignItems="center">
							<EdFab
								onClick={() => mutate('post', 'standalone-courses/sections/create', { standalone_course_id: course.id, classroom_tab_id: tab.id, name: `Section ${tab.sections.length + 1}` }, 'Section created')}
								size="small"
								color="primary"
							>
								<EdIcon fontSize="medium">add</EdIcon>
							</EdFab>
							<Spacer mx="0.75rem" />
							<CurriculumFabLabel>Add Section</CurriculumFabLabel>
						</FlexLayout>
						<Droppable droppableId={`tab-${tab.id}`} type="SECTIONS">
						{(sectionsDrop) => <div ref={sectionsDrop.innerRef} {...sectionsDrop.droppableProps}>
						{tab.sections.map((section, sectionIndex) => (
							<Draggable key={section.id} draggableId={`section-drag-${section.id}`} index={sectionIndex}>
							{(sectionDrag) => <div ref={sectionDrag.innerRef} {...sectionDrag.draggableProps}>
							<StandaloneSectionAccordion defaultExpanded>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<DndHandle {...sectionDrag.dragHandleProps}>↕</DndHandle>
									<InlineEdit prefix={`Section ${sectionIndex + 1}`} initial={section.name} onSave={(name) => mutate('patch', 'standalone-courses/sections/update', { id: section.id, name, order: sectionIndex + 1, classroom_tab_id: tab.id }, 'Section updated')} onDelete={() => mutate('delete', 'standalone-courses/sections/delete', { id: section.id }, 'Section deleted')} />
								</AccordionSummary>
								<AccordionDetails>
									<FlexLayout alignItems="center">
										<EdFab onClick={() => setLectureSection(section.id)} size="small" color="warning">
											<EdIcon fontSize="medium">add</EdIcon>
										</EdFab>
										<Spacer mx="0.75rem" />
										<CurriculumFabLabel>Add Lecture</CurriculumFabLabel>
									</FlexLayout>
									{lectureSection === section.id && (
										<CurriculumLectureRow>
											<TextField size="small" label="Lecture name" value={lectureName} onChange={(event) => setLectureName(event.target.value)} />
											<TextField size="small" label="Lecture code" value={lectureCode} onChange={(event) => setLectureCode(event.target.value)} />
											<Button disabled={!lectureName || !lectureCode} onClick={async () => { await mutate('post', 'standalone-courses/lectures/create', { standalone_course_id: course.id, section_id: section.id, name: lectureName, code: lectureCode, description: '', order: section.courses.length + 1, active: true }, 'Lecture created'); setLectureName(''); setLectureCode(''); setLectureSection(undefined); }}>Save</Button>
											<Button onClick={() => setLectureSection(undefined)}>Cancel</Button>
										</CurriculumLectureRow>
									)}
									<Droppable droppableId={`section-${section.id}`} type="LECTURES">
									{(lecturesDrop) => <div ref={lecturesDrop.innerRef} {...lecturesDrop.droppableProps}>
									{section.courses?.map((lecture, lectureIndex) => (
										<Draggable key={lecture.id} draggableId={`lecture-drag-${lecture.id}`} index={lectureIndex}>
										{(lectureDrag) => <CurriculumLectureRow ref={lectureDrag.innerRef} {...lectureDrag.draggableProps}>
											<DndHandle {...lectureDrag.dragHandleProps}>↕</DndHandle>
											<TextField size="small" value={lecture.name} disabled />
											<TextField size="small" value={lecture.code || '-'} disabled />
											<TextField size="small" value={lectureIndex + 1} disabled />
											<EdActiveStatus active={lecture.active} />
											<Button variant="outlined" startIcon={<AddIcon />} onClick={() => history.push(`/dashboard/classrooms/standalone-courses/${course.label}/lecture/${lecture.id}/edit/content`)}>Content</Button>
											<IconButton onClick={() => mutate('delete', 'standalone-courses/lectures/delete', { id: lecture.id }, 'Lecture deleted')}><DeleteIcon /></IconButton>
										</CurriculumLectureRow>}
										</Draggable>
									))}
									{lecturesDrop.placeholder}</div>}
									</Droppable>
								</AccordionDetails>
							</StandaloneSectionAccordion></div>}
							</Draggable>
						))}
						{sectionsDrop.placeholder}</div>}
						</Droppable>
					</AccordionDetails>
				</StandaloneTabAccordion></div>}
				</Draggable>
			))}
			{tabsDrop.placeholder}</div>}
			</Droppable>
			</DragDropContext>
			{needReorder && <Actions><Button color="warning" variant="contained" onClick={() => { setTabs(course.tabs || []); setNeedReorder(false); }}>Cancel</Button><Button variant="contained" onClick={saveOrder}>Save Order</Button></Actions>}
		</StandaloneCurriculumPage>
	);
};

const InlineEdit: React.FC<{ prefix: string; initial: string; onSave: (value: string) => void; onDelete: () => void }> = ({ prefix, initial, onSave, onDelete }) => {
	const [value, setValue] = useState(initial);
	return <CurriculumInlineForm onClick={(event) => event.stopPropagation()}><CurriculumPrefix>{prefix}</CurriculumPrefix><TextField size="small" value={value} onChange={(event) => setValue(event.target.value)} /><Button disabled={value === initial} onClick={() => onSave(value)}>Save</Button><IconButton onClick={onDelete}><DeleteIcon /></IconButton></CurriculumInlineForm>;
};

const Analytics: React.FC<{ course: StandaloneCourse }> = ({ course }) => {
	const [data, setData] = useState<any>();
	useEffect(() => { api.post('standalone-courses/analytics', { standalone_course_id: course.id }).then((response) => setData(apiData<any>(response))); }, [course.id]);
	if (!data) return null;
	const metrics = {
		total_enrollments: data.enrollments?.total || 0,
		active_enrollments: data.enrollments?.active || 0,
		suspended_enrollments: data.enrollments?.suspended || 0,
		expired_enrollments: data.enrollments?.expired || 0,
		revenue: data.financial?.revenue || 0,
		discounts: data.financial?.discounts || 0,
		refunds: data.financial?.refunded_amount || 0,
		net_revenue: data.financial?.net_revenue || 0,
		wallet_purchases: data.financial?.wallet_purchases || 0,
		scratchcard_purchases: data.financial?.scratchcard_purchases || 0,
		free_enrollments: data.financial?.free_enrollments || 0,
	};
	return <><MetricGrid>{Object.entries(metrics).map(([key, value]) => <Paper key={key} sx={{ padding: 3 }}><Typography color="textSecondary">{key.replaceAll('_', ' ')}</Typography><Typography variant="h4">{String(value)}</Typography></Paper>)}</MetricGrid><Spacer marginBottom="2rem" /><FormPaper><Typography variant="h6">Lecture starts and progress</Typography><Table><TableHead><TableRow><TableCell>Lecture</TableCell><TableCell>Started</TableCell><TableCell>Average progress</TableCell></TableRow></TableHead><TableBody>{(data.lectures || []).map((lecture: any) => <TableRow key={lecture.id}><TableCell>{lecture.name}</TableCell><TableCell>{lecture.started_count}</TableCell><TableCell>{lecture.average_progress || 0}%</TableCell></TableRow>)}</TableBody></Table></FormPaper></>;
};

/**
 * Invoices subtab — the classroom EnrollmentInvoices price-group design
 * (summary money cards + method buckets incl. the derived promocode split).
 * Replaced the flat per-invoice table; the old `standalone-courses/invoices`
 * endpoint stays server-side for deep links.
 */
const Invoices: React.FC<{ course: StandaloneCourse }> = ({ course }) => (
	<ProductEnrollmentInvoices scope="standalone-course" productId={course.id} />
);

const Actions = styled.div`display: flex; justify-content: flex-end; gap: 1rem; margin: 1rem 0 2rem;`;
const FormPaper = styled(Paper)`padding: 2rem; overflow-x: auto;`;
const StandaloneFormContainer = styled.div`
	padding: 1.375rem;
	border-radius: 10px;
	border: 1px solid ${(props) => props.theme.palette.primary.main};
	background: #fff;
`;
const FormTitle = styled.h1`
	margin: 0 0 0.25rem;
	font-size: 1.125rem;
	font-weight: 700;
	line-height: 1.5;
`;
const FormGrid = styled(GridLayout)`grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 2rem; margin: 1.5rem 0; @media (max-width: 760px) { grid-template-columns: 1fr; }`;
const CoverGrid = styled(GridLayout)`
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 2rem;
	margin: 1.5rem 0;
	@media (max-width: 760px) {
		grid-template-columns: 1fr;
	}
`;
const SwitchGrid = styled.div`display: flex; gap: 2rem; flex-wrap: wrap; margin: 1.5rem 0;`;
const ManagementTitle = styled.h2`
	font-size: 1.25rem;
	font-weight: bold;
	margin: 2.875rem 0 2.313rem 2.125rem;
`;
const LectureRow = styled.div`display: flex; align-items: center; gap: 1rem; margin: .75rem 0;`;
const MetricGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 1rem;`;
const DialogFields = styled.div`
	display: grid;
	gap: 1rem;
	padding-top: 0.5rem;
`;
const DialogActionsRow = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 0.75rem;
`;
const StandaloneCurriculumPage = styled.div`
	padding: 0 6.25rem;
	@media (max-width: 900px) { padding: 0 1rem; }
`;
const CurriculumFabLabel = styled.span`
	color: #adadad;
	font-size: 0.875rem;
`;
const StandaloneTabAccordion = styled(Accordion)`
	margin: 2rem 0;
	background-color: ${(props) => props.theme.palette.grey[100]};
	&.Mui-expanded:last-of-type { margin: 2rem 0; }
	.MuiAccordionSummary-content { width: 100%; }
`;
const StandaloneSectionAccordion = styled(Accordion)`
	margin: 1rem 0;
	padding: 0.5rem 0 1rem 0.406rem;
	border: 1px solid ${(props) => props.theme.palette.grey[600]};
	background-color: ${(props) => props.theme.palette.grey[100]};
	box-shadow: none;
	&.Mui-expanded:last-of-type { margin: 1rem 0; }
	.MuiAccordionSummary-content { width: 100%; }
`;
const CurriculumInlineForm = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
	width: 100%;
	padding-right: 1rem;
	.MuiTextField-root { max-width: 32rem; }
`;
const CurriculumPrefix = styled.span`
	font-size: 0.875rem;
	min-width: 7.125rem;
`;
const DndHandle = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 1.5rem;
	cursor: grab;
	font-size: 1.25rem;
	user-select: none;
`;
const CurriculumLectureRow = styled(Paper)`
	display: grid;
	grid-template-columns: auto minmax(12rem, 1.5fr) minmax(10rem, 1fr) minmax(5rem, 0.5fr) auto auto auto;
	align-items: center;
	gap: 1rem;
	margin: 1rem 0 1rem 17%;
	padding: 0.5rem 1rem;
	@media (max-width: 1100px) {
		margin-left: 0;
		grid-template-columns: 1fr;
	}
`;
const StyledEditIcon = styled(EditIcon)`
	color: ${(props) => props.theme.palette.darkPurple.main};
	&:hover { color: ${(props) => props.theme.palette.mayaBlue.main}; }
`;
const StyledDeleteIcon = styled(DeleteIcon)`
	color: ${(props) => props.theme.palette.darkPurple.main};
	&:hover { color: ${(props) => props.theme.palette.mayaBlue.main}; }
`;
const StatusIcon = styled(EdIcon)`
	width: 26px;
	height: 26px;
	border-radius: 5px;
	display: flex !important;
	justify-content: center;
	align-items: center;
	line-height: 1.28 !important;
	font-weight: bold;
	font-size: 1.7rem;
`;
const ActiveCheckIcon = styled(StatusIcon)`
	background: #5ac0fc;
	color: #fff;
	border: 2px solid #5ac0fc;
`;
const InactiveCloseIcon = styled(StatusIcon)`
	background: #ff3100;
	color: #fff;
	border: 2px solid #ff3100;
`;

export default StandaloneCourses;
